import { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import sortBy from 'lodash.sortby';
import LocationAutocomplete from '@features/LocationAutocomplete';
import GoogleMap, { DEFAULT_MAP_CENTER, resolveCoordinate } from '@components/GoogleMap';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import Autocomplete from '@components/Autocomplete';
import { useTranslation, useYupValidationResolver, useThrottleValue } from '@hooks';
import { accountActions, alertActions, authActions, googleActions } from '@actions';
import { companyService } from '@services';
import { LocationType } from '@constants/companies';
import { LOCALE } from '@constants/locale';
import { Industry } from '@constants/account';
import { handleApiError } from '@helpers/api';
import { preparePhoneNumberForSubmit, setInitialPhoneNumberToForm } from '@helpers/phone';
import {
    Modal,
    Form,
    StyledRadioGroup,
    StyledSwitch,
    StyledPhoneInput,
    GooglePlacesSection,
    FullWidthField,
} from './styles';
import { getAddLocationSchema } from './formConfig';

const DEFAULT_FORM_VALUES = {
    creationMode: 'google',
    placeId: '',
    internalName: '',
    locationName: '',
    type: undefined,
    primaryCategory: [],
    phoneNumber: '',
    country: '',
    hasBusinessAddress: true,
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    postalCode: '',
    latitude: '',
    longitude: '',
};

const AddLocationModal = ({ isOpen, onClose, setSelectedCompany, changeView }) => {
    const [creationMode, setCreationMode] = useState('google'); // 'google' | 'manual'
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [mapInited, setMapInited] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [categorySearch, setCategorySearch] = useState('');
    const hasInitializedFormRef = useRef(false);

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mapRef = useRef();

    const account = useSelector(state => state.account.account);
    const user = useSelector(state => state.auth.user);
    const { data: countries } = useSelector(state => state.countries);
    const { searchedCategories } = useSelector(state => state.google);

    const locale = account?.locale;
    const industry = account?.industry;
    const language = user?.language;

    const defaultLocationType =
        industry === Industry.Restaurant
            ? LocationType.Restaurant
            : industry === Industry.HotelAndLodging
              ? LocationType.Hotel
              : undefined;

    const schema = useMemo(() => getAddLocationSchema(), []);
    const resolver = useYupValidationResolver(schema);

    const methods = useForm({
        mode: 'all',
        resolver,
        defaultValues: DEFAULT_FORM_VALUES,
    });

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        reset,
        trigger,
        formState: { isValid, isSubmitting },
    } = methods;

    const hasBusinessAddress = watch('hasBusinessAddress');
    const country = watch('country');
    const latitude = watch('latitude');
    const longitude = watch('longitude');
    const primaryCategory = watch('primaryCategory');

    useEffect(() => {
        if (country && countries) {
            setSelectedCountry(countries.find(item => item.iso2 === country));
        }
    }, [country, countries]);

    // Preset phone number once when modal opens (not on every dependency change)
    useEffect(() => {
        if (isOpen && !hasInitializedFormRef.current && locale) {
            reset({
                ...DEFAULT_FORM_VALUES,
                type: defaultLocationType,
                phoneNumber: setInitialPhoneNumberToForm('', locale),
            });
            hasInitializedFormRef.current = true;
        }

        if (!isOpen) {
            hasInitializedFormRef.current = false;
        }
    }, [isOpen, locale, reset, defaultLocationType]);

    // Handle category search with throttle
    const throttledCategorySearch = useThrottleValue(categorySearch, 1000);

    useEffect(() => {
        if (throttledCategorySearch.length === 0) {
            dispatch(googleActions.resetSearchedCategories());
            return;
        }
        if (throttledCategorySearch.length >= 2) {
            dispatch(googleActions.getSearchedCategories(throttledCategorySearch));
        }
    }, [throttledCategorySearch, dispatch]);

    const availableCategories = useMemo(() => {
        const selectedCategoryId = primaryCategory?.[0]?.id;
        const selectedCategory = primaryCategory?.[0];

        const filtered = sortBy(
            searchedCategories.filter(category => category.id !== selectedCategoryId),
            'displayName',
        );

        // Include selected category in options for MUI validation
        return selectedCategory ? [selectedCategory, ...filtered] : filtered;
    }, [searchedCategories, primaryCategory]);

    // Clear form when switching modes
    const handleModeChange = mode => {
        setCreationMode(mode);

        if (mode === 'google') {
            // Clear manual form fields
            reset({
                ...DEFAULT_FORM_VALUES,
                creationMode: 'google',
                type: defaultLocationType,
                phoneNumber: setInitialPhoneNumberToForm('', locale),
            });
            dispatch(googleActions.resetSearchedCategories());
        } else {
            // Clear Google selection and reset for manual mode
            reset({
                ...DEFAULT_FORM_VALUES,
                creationMode: 'manual',
                type: defaultLocationType,
                phoneNumber: setInitialPhoneNumberToForm('', locale),
            });
            setSelectedPlace(null);
            setCategorySearch('');
        }
    };

    const handleGooglePlaceSelect = option => {
        const { place_id: placeId } = option;
        if (placeId) {
            setSelectedPlace(option);
            setValue('placeId', placeId, { shouldValidate: true });
        }
    };

    const onClearPlace = () => {
        setSelectedPlace(null);
        setValue('placeId', '', { shouldValidate: true });
    };

    const onSubmit = async () => {
        const values = getValues();

        try {
            let newCompany;

            if (creationMode === 'google') {
                // Google Places flow - send only placeId
                newCompany = await companyService.addCompany(values.placeId);
            } else {
                // Manual entry flow - send full location object
                const categories =
                    values.primaryCategory?.map(cat => ({
                        id: cat.id,
                        isPrimary: true,
                    })) || [];

                const locationData = {
                    internalName: values.internalName,
                    locationName: values.locationName,
                    type: values.type,
                    categories,
                    phoneNumber: preparePhoneNumberForSubmit(values.phoneNumber, true),
                    country: values.country,
                    hasBusinessAddress: values.hasBusinessAddress,
                    addressLine1: values.addressLine1,
                    city: values.city,
                    region: values.region,
                    postalCode: values.postalCode,
                    latitude: values.latitude || null,
                    longitude: values.longitude || null,
                };

                newCompany = await companyService.addCompany(locationData);
            }

            await dispatch(accountActions.get());
            await dispatch(authActions.getCurrentUser());
            dispatch(alertActions.success(t('alertMessages.createSuccess')));
            setSelectedCompany(newCompany);
            onClose();
            changeView();
        } catch (error) {
            handleApiError(error, t('alertMessages.createFail'));
        }
    };

    const handleClose = () => {
        setCreationMode('google');
        setSelectedPlace(null);
        setCategorySearch('');
        dispatch(googleActions.resetSearchedCategories());
        onClose();
    };

    // Using exact same logic as GeneralInfo.jsx
    const typeOptions = useMemo(
        () => [
            { value: LocationType.Location, label: t('common.location') },
            ...(industry === Industry.HealthcareAndDental
                ? [{ value: LocationType.HealthcareFacility, label: t('SettingsCompanies.healthcareFacility') }]
                : []),
            ...(industry === Industry.HealthcareAndDental && locale === LOCALE.UNITED_STATES
                ? [{ value: LocationType.HealthcareProfessional, label: t('SettingsCompanies.healthcareProfessional') }]
                : []),
            ...([Industry.HotelAndLodging, Industry.Restaurant, Industry.Other].includes(industry)
                ? [{ value: LocationType.Hotel, label: t('SettingsCompanies.hotel') }]
                : []),
            ...([Industry.HotelAndLodging, Industry.Restaurant, Industry.FoodAndGrocery, Industry.Other].includes(
                industry,
            )
                ? [{ value: LocationType.Restaurant, label: t('SettingsCompanies.restaurant') }]
                : []),
        ],
        [industry, locale, t],
    );

    const countriesOptions = useMemo(
        () =>
            (countries || []).map(item => ({
                value: item.iso2,
                label: item.translations?.[language?.toLowerCase()] || item.name,
            })),
        [countries, language],
    );

    // Using exact same logic as Address.jsx
    const regionsOptions = useMemo(
        () => (selectedCountry ? selectedCountry.states.map(item => ({ value: item.name, label: item.name })) : []),
        [selectedCountry],
    );

    const isSubmitDisabled = isSubmitting || !isValid || (creationMode === 'google' && !selectedPlace);

    return (
        <>
            <Modal
                fullScreen
                isOpen={isOpen}
                handleClose={handleClose}
                title={t('SettingsCompanies.addLocationTitle')}
                onPrimaryAction={handleSubmit(onSubmit)}
                primaryActionText={t('buttons.addLocation')}
                primaryActionDisabled={isSubmitDisabled}
            >
                <FormProvider {...methods}>
                    <StyledRadioGroup value={creationMode} onChange={e => handleModeChange(e.target.value)}>
                        <FormControlLabel
                            value="google"
                            control={<Radio />}
                            label={t('SettingsCompanies.findOnGoogle')}
                        />
                        <FormControlLabel
                            value="manual"
                            control={<Radio />}
                            label={t('SettingsCompanies.enterManually')}
                        />
                    </StyledRadioGroup>

                    {/* Google Places Path */}
                    {creationMode === 'google' && (
                        <GooglePlacesSection>
                            {mapInited && (
                                <LocationAutocomplete
                                    label={t('SettingsCompanies.googleAutocomplete')}
                                    onSelectOption={handleGooglePlaceSelect}
                                    autocompleteRequestOptions={{
                                        fields: ['address_components', 'geometry', 'icon', 'name'],
                                        types: ['establishment'],
                                        strictBounds: true,
                                    }}
                                    onClearPlace={onClearPlace}
                                />
                            )}
                            <GoogleMap
                                id="add-location-map"
                                options={{ center: DEFAULT_MAP_CENTER, zoom: 17 }}
                                onMapLoad={map => {
                                    mapRef.current = map;
                                    setMapInited(true);
                                }}
                                style={{ display: 'none' }}
                            />
                        </GooglePlacesSection>
                    )}

                    {/* Manual Entry Path */}
                    {creationMode === 'manual' && (
                        <Form>
                            <Controller
                                control={control}
                                name="internalName"
                                render={({ field, fieldState: { error } }) => (
                                    <Input
                                        inputProps={field}
                                        label={t('SettingsCompanies.internalName')}
                                        fullWidth
                                        error={!!error}
                                        helperText={error ? t(error.message) : null}
                                        required
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="locationName"
                                render={({ field, fieldState: { error } }) => (
                                    <Input
                                        inputProps={field}
                                        label={t('SettingsCompanies.locationName')}
                                        fullWidth
                                        error={!!error}
                                        helperText={error ? t(error.message) : null}
                                        required
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="type"
                                render={({ field, fieldState: { error } }) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? ''}
                                        onChange={({ target: { value } }) => field.onChange(value)}
                                        options={typeOptions}
                                        label={t('common.type')}
                                        fullWidth
                                        error={!!error}
                                        helperText={error ? t(error.message) : null}
                                        required
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="primaryCategory"
                                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                    <Autocomplete
                                        {...field}
                                        inputRef={ref}
                                        label={t('Listings.primaryCategory')}
                                        options={availableCategories}
                                        value={field.value || []}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        optionLabelProp="displayName"
                                        inputProps={{
                                            value: categorySearch,
                                            onChange: e => setCategorySearch(e.target.value),
                                            style: { display: field?.value?.length > 0 ? 'none' : 'block' },
                                        }}
                                        onChangeAutoComplete={items => {
                                            setValue('primaryCategory', items.length ? items : [], {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                            setCategorySearch('');
                                        }}
                                        required={true}
                                        error={error}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="country"
                                render={({ field, fieldState: { error } }) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? ''}
                                        onChange={({ target: { value } }) => {
                                            field.onChange(value);
                                            setValue('region', '');
                                        }}
                                        options={countriesOptions}
                                        label={t('SettingsCompanies.country')}
                                        fullWidth
                                        error={!!error}
                                        helperText={error ? t(error.message) : null}
                                        required
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="phoneNumber"
                                render={({ field, fieldState: { error } }) => (
                                    <StyledPhoneInput
                                        placeholder=""
                                        label={t('SettingsCompanies.primaryPhone')}
                                        value={field.value}
                                        autoFormat
                                        onChange={field.onChange}
                                        error={error ? t(error.message) : null}
                                        required
                                    />
                                )}
                            />

                            <FullWidthField>
                                <Controller
                                    control={control}
                                    name="hasBusinessAddress"
                                    render={({ field }) => (
                                        <StyledSwitch
                                            {...field}
                                            checked={field.value}
                                            handleChange={event => {
                                                field.onChange(event.target.checked);
                                                trigger(['addressLine1', 'city', 'postalCode']);
                                            }}
                                            label={t('SettingsCompanies.showAddress')}
                                        />
                                    )}
                                />
                            </FullWidthField>

                            {hasBusinessAddress && (
                                <>
                                    <FullWidthField style={{ marginTop: 8 }}>
                                        <GoogleMap
                                            id="address-map"
                                            options={{
                                                center: {
                                                    lat: resolveCoordinate(latitude, DEFAULT_MAP_CENTER.lat),
                                                    lng: resolveCoordinate(longitude, DEFAULT_MAP_CENTER.lng),
                                                },
                                                zoom: 17,
                                            }}
                                            onMapLoad={map => {
                                                mapRef.current = map;
                                            }}
                                            style={{ height: 400, width: '100%' }}
                                            onChange={value => {
                                                setValue('latitude', value.latitude, { shouldDirty: true });
                                                setValue('longitude', value.longitude, { shouldDirty: true });
                                            }}
                                        />
                                    </FullWidthField>

                                    <FullWidthField>
                                        <Controller
                                            control={control}
                                            name="addressLine1"
                                            render={({ field, fieldState: { error } }) => (
                                                <Input
                                                    inputProps={field}
                                                    label={t('SettingsCompanies.addressLine1')}
                                                    fullWidth
                                                    error={!!error}
                                                    helperText={error ? t(error.message) : null}
                                                    required
                                                />
                                            )}
                                        />
                                    </FullWidthField>

                                    <Controller
                                        control={control}
                                        name="city"
                                        render={({ field, fieldState: { error } }) => (
                                            <Input
                                                inputProps={field}
                                                label={t('SettingsCompanies.city')}
                                                fullWidth
                                                error={!!error}
                                                helperText={error ? t(error.message) : null}
                                                required
                                            />
                                        )}
                                    />

                                    <Controller
                                        control={control}
                                        name="region"
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                value={field.value ?? ''}
                                                onChange={({ target: { value } }) => field.onChange(value)}
                                                options={regionsOptions}
                                                label={t('SettingsCompanies.state')}
                                                fullWidth
                                            />
                                        )}
                                    />

                                    <Controller
                                        control={control}
                                        name="postalCode"
                                        render={({ field, fieldState: { error } }) => (
                                            <Input
                                                inputProps={field}
                                                label={t('SettingsCompanies.postalCode')}
                                                fullWidth
                                                error={!!error}
                                                helperText={error ? t(error.message) : null}
                                                required
                                            />
                                        )}
                                    />
                                </>
                            )}
                        </Form>
                    )}
                </FormProvider>
            </Modal>
        </>
    );
};

export default AddLocationModal;
